import { useEffect, useState, type FormEvent } from 'react';

import { useChat } from '../hooks/useChat';
import useAuth from '../hooks/useAuth';
import { api } from '../services/api';
import type { AccountSummary } from '../types';
import MediaGenerationSettings from '../components/Settings/MediaGenerationSettings';
import NvidiaTaskCredentialsCard from '../components/Settings/NvidiaTaskCredentialsCard';
import DevicePairing from '../components/Settings/DevicePairing';
import LinkedDevices from '../components/Settings/LinkedDevices';
import WhatsAppAccounts from '../components/Settings/WhatsAppAccounts';
import VoiceSettingsPanel from '../components/Settings/VoiceSettings';
import SocialPlatformSettings from '../components/Settings/SocialPlatformSettings';
import { InfoTooltip } from '../components/Common/InfoTooltip';

const Settings = () => {
  const {
    settings,
    features,
    setFeatures,
    systemStatus,
    setGlobalAutomode,
    reload,
    offline,
    realtimeConnected,
  } = useChat();
  const { user, changePassword } = useAuth();
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    newPasswordConfirm: '',
  });
  const [passwordNotice, setPasswordNotice] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [togglingFeature, setTogglingFeature] = useState<string | null>(null);
  const [csvText, setCsvText] = useState('');
  const [contactNotice, setContactNotice] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);
  const [importingContacts, setImportingContacts] = useState(false);

  const changeAutomode = async (enabled: boolean) => {
    setSaving(true);
    setNotice(null);
    try {
      await setGlobalAutomode(enabled);
      setNotice(enabled ? 'Globale automode ingeschakeld.' : 'Globale automode uitgeschakeld.');
    } catch {
      setNotice('Automode kon niet worden aangepast.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') return;
    void api.features().then(setFeatures).catch(() => undefined);
    void api.accounts().then(setAccounts).catch(() => undefined);
  }, [user?.role, setFeatures]);

  const reloadAccounts = () => {
    void api.accounts().then(setAccounts).catch(() => undefined);
  };

  const handleReload = async () => {
    setLastRefresh(null);
    await reload();
    setLastRefresh(new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
  };

  const toggleFeature = async (flag: string) => {
    setTogglingFeature(flag);
    try {
      const currentValue = features?.[flag] ?? false;
      const updated = await api.toggleFeature(flag, !currentValue);
      setFeatures(updated);
    } catch {
      setNotice('Feature toggle kon niet worden aangepast.');
    } finally {
      setTogglingFeature(null);
    }
  };

  const exportContacts = async () => {
    setContactNotice(null);
    try {
      const csv = await api.contactsExport();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contacts-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setContactNotice('Export mislukt.');
    }
  };

  const importContacts = async () => {
    setImportingContacts(true);
    setContactNotice(null);
    try {
      const result = await api.contactsImport(csvText);
      setCsvText('');
      setContactNotice(
        `${result.created} contacten geïmporteerd.${result.errors.length > 0 ? ` Fouten: ${result.errors.join('; ')}` : ''}`,
      );
    } catch (reason: unknown) {
      const message = reason instanceof Error ? reason.message : 'Import mislukt.';
      setContactNotice(message);
    } finally {
      setImportingContacts(false);
    }
  };

  const submitPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setPasswordNotice(null);

    if (passwordForm.newPassword !== passwordForm.newPasswordConfirm) {
      setPasswordNotice('Het nieuwe wachtwoord en de herhaling komen niet overeen.');
      setSaving(false);
      return;
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordNotice('Het nieuwe wachtwoord moet anders zijn dan het huidige wachtwoord.');
      setSaving(false);
      return;
    }

    try {
      await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        newPasswordConfirm: passwordForm.newPasswordConfirm,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', newPasswordConfirm: '' });
      setPasswordNotice('Wachtwoord gewijzigd. Andere sessies zijn uitgelogd.');
    } catch (reason: unknown) {
      const message =
        reason instanceof Error ? reason.message : 'Wachtwoord kon niet worden gewijzigd.';
      setPasswordNotice(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="content-page">
      <header className="page-heading">
        <p className="eyebrow">Configuratie</p>
        <h1>Instellingen en status</h1>
        <p>
          API-sleutels worden uitsluitend op de backend via omgevingsvariabelen
          beheerd en zijn nooit zichtbaar in deze client.
        </p>
      </header>
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      <div className="settings-grid">
        <section className="settings-card">
          <div className="setting-heading">
            <div>
              <h2>
                Globale automode
                <InfoTooltip text="Dit is de hoofdschakelaar. Aan = AI mag automatisch antwoorden in chats waar per-chat automode op 'Inherit' of 'Aan' staat. Uit = geen automatische AI-replies, ongeacht per-chat instellingen." />
              </h2>
              <p>
                De backend controleert daarnaast toestemming, chatpauze, het
                24-uursvenster, providerstatus en veiligheid.
              </p>
            </div>
            <label className="switch">
              <span className="sr-only">Globale automode</span>
              <input
                type="checkbox"
                checked={settings?.globalAutomode ?? false}
                disabled={saving || !settings}
                onChange={(event) => void changeAutomode(event.target.checked)}
              />
              <span aria-hidden="true" />
            </label>
          </div>
        </section>
        <WhatsAppAccounts accounts={accounts} onReload={reloadAccounts} />
        <VoiceSettingsPanel />
        <SocialPlatformSettings />
        {user?.role === 'admin' && <MediaGenerationSettings />}
        {user?.role === 'admin' && <NvidiaTaskCredentialsCard />}
        {user?.role === 'admin' && features && (
          <section className="settings-card">
            <div className="setting-heading">
              <div>
                <h2>Feature toggles</h2>
                <p>Beheer optionele functies. Wijzigingen worden gelogd.</p>
              </div>
            </div>
            <div className="feature-toggles">
              {Object.entries(features).map(([flag, enabled]: [string, boolean]) => (
                <div key={flag} className="feature-toggle-row">
                  <span>{flag}</span>
                  <label className="switch">
                    <span className="sr-only">{flag}</span>
                    <input
                      type="checkbox"
                      checked={enabled}
                      disabled={togglingFeature === flag}
                      onChange={() => void toggleFeature(flag)}
                    />
                    <span aria-hidden="true" />
                  </label>
                </div>
              ))}
            </div>
          </section>
        )}
        <section className="settings-card">
          <div className="setting-heading">
            <div>
              <h2>Wachtwoord</h2>
              <p>Alle andere actieve sessies worden uitgelogd na een wachtwoordwijziging.</p>
            </div>
          </div>
          <form onSubmit={(event) => void submitPassword(event)}>
            <div className="form-group">
              <label htmlFor="current-password">Huidig wachtwoord</label>
              <input
                id="current-password"
                type="password"
                value={passwordForm.currentPassword}
                onChange={(event) =>
                  setPasswordForm((prev) => ({ ...prev, currentPassword: event.target.value }))
                }
                required
                minLength={1}
              />
            </div>
            <div className="form-group">
              <label htmlFor="new-password">Nieuw wachtwoord</label>
              <input
                id="new-password"
                type="password"
                value={passwordForm.newPassword}
                onChange={(event) =>
                  setPasswordForm((prev) => ({ ...prev, newPassword: event.target.value }))
                }
                required
                minLength={12}
                maxLength={128}
              />
            </div>
            <div className="form-group">
              <label htmlFor="new-password-confirm">Herhaal nieuw wachtwoord</label>
              <input
                id="new-password-confirm"
                type="password"
                value={passwordForm.newPasswordConfirm}
                onChange={(event) =>
                  setPasswordForm((prev) => ({ ...prev, newPasswordConfirm: event.target.value }))
                }
                required
                minLength={12}
                maxLength={128}
              />
            </div>
            {passwordNotice && (
              <p className={`notice ${passwordNotice.includes('gewijzigd') ? '' : 'warning'}`} role="status">
                {passwordNotice}
              </p>
            )}
            <button type="submit" className="button primary small" disabled={saving}>
              Wijzig wachtwoord
            </button>
          </form>
        </section>
        <section className="settings-card">
          <div className="setting-heading">
            <div>
              <h2>Applicatie</h2>
              <p>Versie {systemStatus?.server.version ?? '—'}</p>
            </div>
            <StatusBadge
              ready={!offline && realtimeConnected}
              text={offline ? 'offline' : realtimeConnected ? 'realtime' : 'verbinden'}
            />
          </div>
          <div className="form-row align-center">
            <button type="button" className="button secondary small" onClick={() => void handleReload()}>
              Status vernieuwen
            </button>
            {lastRefresh && (
              <span className="muted small">Vernieuwd om {lastRefresh}</span>
            )}
          </div>
        </section>
        <section className="settings-card">
          <div className="setting-heading">
            <div>
              <h2>Contacten</h2>
              <p>Importeer of exporteer contacten als CSV.</p>
            </div>
          </div>
          <div className="form-group">
            <button
              type="button"
              className="button secondary small"
              onClick={() => void exportContacts()}
            >
              Contacten exporteren
            </button>
          </div>
          <div className="form-group">
            <label htmlFor="contacts-csv">CSV contacten</label>
            <textarea
              id="contacts-csv"
              rows={6}
              value={csvText}
              onChange={(event) => setCsvText(event.target.value)}
              placeholder="phone,name,relationshipType,tags,notes,blocked,optInMarketing,optInUpdates\n+31612345678,Jan,client,,,false,false,false"
            />
          </div>
          {contactNotice && (
            <p className={`notice ${contactNotice.includes('Fouten') ? 'warning' : ''}`} role="status">
              {contactNotice}
            </p>
          )}
          <button
            type="button"
            className="button primary small"
            disabled={importingContacts || !csvText.trim()}
            onClick={() => void importContacts()}
          >
            {importingContacts ? 'Importeren…' : 'Contacten importeren'}
          </button>
        </section>
        <section className="settings-card">
          <div className="setting-heading">
            <div>
              <h2>Apparaten</h2>
              <p>Koppel je telefoon of ander apparaat via QR-code.</p>
            </div>
          </div>
          <DevicePairing />
          <LinkedDevices />
        </section>
      </div>
    </main>
  );
};

const StatusBadge = ({ ready, text }: { ready: boolean; text: string }) => (
  <span className={`status-pill ${ready ? 'success' : 'warning'}`}>
    <span aria-hidden="true">●</span> {text}
  </span>
);

export default Settings;
